"use client";

import { ComponentType, useEffect } from "react";

/**
 * HOC that locks scroll while the wrapped component is mounted
 * @param WrappedComponent - The component to wrap with scroll lock functionality
 * @returns Enhanced component with scroll lock behavior
 */
export const withScrollLock = <P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> => {
  const ScrollLockWrapper = (props: P) => {
    useEffect(() => {
      // Lock scroll and save current position
      document.body.style.overflow = "hidden";
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.top = `-${window.scrollY}px`;
      document.body.dataset.scrollY = String(window.scrollY);
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.paddingRight = `${scrollBarWidth}px`;

      return () => {
        // Restore scroll and position
        const scrollY = document.body.dataset.scrollY;
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.paddingRight = "";
        delete document.body.dataset.scrollY;
        window.scrollTo({ top: Number(scrollY), behavior: "instant" });
      };
    }, []);

    return <WrappedComponent {...props} />;
  };

  ScrollLockWrapper.displayName = `withScrollLock(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

  return ScrollLockWrapper;
};
